import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import Autocomplete from "react-google-autocomplete";
import validator from "validator";
import { getHtmlText, formatStringToRange } from "bundles/common/utils/utils";
import RichTextEditor from "bundles/common/components/RichTextEditor";
import CardSelect from "bundles/common/components/CardSelect";
import ChipSelection from "bundles/common/components/ChipSelection";
import JobOfferSummary from "bundles/Recruiter/components/JobOfferSummary";
import JobOfferMediaFields from "bundles/Recruiter/components/JobOfferMediaFields";
import { uploadJobOfferMediaToS3 } from "bundles/common/utils/uploadJobOfferMedia";
import { jobOfferAdvantages } from "bundles/common/utils/constants";
import InfoIcon from "@mui/icons-material/Info";
import { Check } from "lucide-react";
import { useTheme } from "@mui/styles";
import { Slider } from "@mui/material";
import { FormGroup, Checkbox } from "@mui/material";
import {
  Grid,
  Box,
  TextField,
  Button,
  Typography,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
  Alert,
  MenuItem,
} from "@mui/material";
import ProgressStepper from "bundles/common/components/ProgressStepper";
import DateSelect from "bundles/common/components/DateSelect";
import {
  jobOfferLanguages,
  localLanguages,
  companyIndustryMap,
  offerStatuses,
} from "bundles/common/utils/constants";
import SkillSelect from "../../common/components/SkillSelect";
import { useEffect } from "react";
import { formatJobOfferLocation } from "../../common/utils/utils";
import { normalizeRegion } from "bundles/common/utils/utils";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
  INDUSTRY_CATEGORIES as INDUSTRY_SECTOR_CATEGORIES,
} from "bundles/common/utils/categoryData";

export default function JobOfferEdit({
  setJobOffer,
  jobOffer,
  setSkills,
  skills,
  saveJobOffer,
  setActiveBlocker
}) {
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  const intl = useIntl();

  const stepperSteps = ["1", "2", "3", "4"];
  const stepperLabels = {
    "1": "job_offer_creation.step_basic",
    "2": "job_offer_creation.step_description",
    "3": "job_offer_creation.step_skills",
    "4": "job_offer_creation.step_review",
  };

  // Layout-only spacing helpers (no colors/typography here — those are inlined at each usage).
  const formItemSx = { mb: "16px", overflowX: "auto" };
  const textareaItemSx = { mb: "32px", textAlign: "left" };
  // Consistent rounded/green-focus styling for outlined inputs across the wizard.
  const textFieldSx = {
    minWidth: { xs: "100%", md: "280px" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "hsl(153, 68%, 25%)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "hsl(153, 68%, 25%)",
    },
  };
  // Slim green slider with a filled thumb.
  const sliderSx = {
    color: "hsl(153, 68%, 25%)",
    height: 4,
    py: "12px",
    "& .MuiSlider-rail": {
      backgroundColor: "hsl(160, 10%, 88%)",
      opacity: 1,
    },
    "& .MuiSlider-track": { border: "none" },
    "& .MuiSlider-thumb": {
      height: 18,
      width: 18,
      backgroundColor: "hsl(153, 68%, 25%)",
      "&:hover, &.Mui-focusVisible": {
        boxShadow: "0 0 0 8px hsla(153, 68%, 25%, 0.16)",
      },
    },
    "& .MuiSlider-valueLabel": {
      backgroundColor: "hsl(178, 93%, 10%)",
      borderRadius: "8px",
      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    },
  };
  // Green tinted pill used to highlight the current slider value.
  const valuePillSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    px: "16px",
    py: "6px",
    borderRadius: "9999px",
    backgroundColor: "hsla(153, 68%, 25%, 0.1)",
    color: "hsl(153, 68%, 25%)",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontSize: "1rem",
  };
  const sliderBoundSx = {
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.75rem",
    color: "hsl(178, 10%, 45%)",
  };
  // Consistent button label typography (normal case, Segoe Semibold).
  const buttonSx = {
    textTransform: "none",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontSize: "1rem",
  };
  // Custom rounded checkbox icons (unchecked outline / green-filled with check).
  const checkboxIcon = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "6px",
        border: "2px solid hsl(160, 10%, 80%)",
        boxSizing: "border-box",
        transition: "border-color 0.2s",
      }}
    />
  );
  const checkboxCheckedIcon = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "6px",
        backgroundColor: "hsl(153, 68%, 25%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Check size={14} color="#fff" strokeWidth={3} />
    </Box>
  );

  // Red asterisk appended to a required field's label/heading.
  const RequiredMark = () => (
    <Box
      component="span"
      aria-hidden="true"
      sx={{ color: "hsl(0, 72%, 51%)", ml: "2px" }}
    >
      *
    </Box>
  );

  const initialValidity = () => {
    if (jobOffer?.days_validity) {
      return jobOffer?.days_validity;
    } else {
      return 90;
    }
  };

  const [value, setValue] = useState(() => {
    let annualyJobOffer = jobOffer?.monthly_offer;
    if (annualyJobOffer == null || annualyJobOffer == []) {
      return [0, 0];
    } else if (annualyJobOffer) {
      return formatStringToRange(annualyJobOffer)
        .split("-")
        .map((value) => {
          return parseInt(value);
        });
    } else {
      return [25, 35];
    }
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const [advantages, setAdvantages] = useState(
    jobOffer?.offer_advantages?.advantages || []
  );
  const [environments, setEnvironments] = useState(
    jobOffer?.offer_advantages?.environments || []
  );
  const [schedules, setSchedules] = useState(
    jobOffer?.offer_advantages?.schedules || []
  );
  const [primes, setPrimes] = useState(
    jobOffer?.offer_advantages?.primes || []
  );

  const handleCheck = (value) => {
    if (advantages.includes(value)) {
      setAdvantages(advantages.filter((advantage) => advantage != value));
    } else {
      setAdvantages(advantages.concat(value));
    }
  };

  const handleCheckEnvironment = (value) => {
    if (environments.includes(value)) {
      setEnvironments(environments.filter((advantage) => advantage != value));
    } else {
      setEnvironments(environments.concat(value));
    }
  };

  const handleCheckSchedules = (value) => {
    if (schedules.includes(value)) {
      setSchedules(schedules.filter((advantage) => advantage != value));
    } else {
      setSchedules(schedules.concat(value));
    }
  };

  const handleCheckPrimes = (value) => {
    if (primes.includes(value)) {
      setPrimes(primes.filter((advantage) => advantage != value));
    } else {
      setPrimes(primes.concat(value));
    }
  };

  const initialFormValues = {
    id: jobOffer?.id || "",
    offer_language: (() => {
      const raw =
        jobOffer?.offer_language ||
        localLanguages.find(
          (local) => local.value === currentRecruiter.preferred_locale
        )?.longValue ||
        "";
      return raw === "french_france" ? "french" : raw;
    })(),
    company_industries:
      jobOffer?.company_industries || [
        currentRecruiter.company.company_industry,
      ] ||
      [],
    categories: jobOffer?.categories || [],
    job_title: jobOffer?.job_title || "",
    job_statuses: jobOffer?.job_statuses || [],
    locations:
      jobOffer && jobOffer.locations && jobOffer.locations.length > 0
        ? jobOffer?.locations?.map((location) => {
          return {
            zipcode: location?.zipcode || "",
            city: location?.city || "",
            region: location?.region || "",
            district: location?.district || "",
            country: location?.country || "",
          };
        })
        : [],
    monthly_offer: jobOffer?.monthly_offer || value,
    start_date: jobOffer?.start_date
      ? new Date(jobOffer.start_date)?.toISOString().slice(0, 10)
      : "",
    end_date: jobOffer?.end_date
      ? new Date(jobOffer.end_date)?.toISOString().slice(0, 10)
      : "",
    end_of_mandate: jobOffer?.end_of_mandate
      ? new Date(jobOffer.end_of_mandate)?.toISOString().slice(0, 10)
      : "",
    published_at: jobOffer?.published_at || "",
    offer_status: jobOffer?.offer_status || "",
    offer_advantages: jobOffer?.offer_advantages || [],
    external_application: jobOffer?.external_application || false,
    external_application_url: jobOffer?.external_application_url || "",
    days_validity: initialValidity(),
  };

  const stepValues = {
    0: {
      validationfields: [
        "job_title",
        "offer_language",
        "company_industries",
        "categories",
        "job_statuses",
        "start_date",
        "end_date",
        "locations",
      ],
    },
    1: {
      validationfields: ["description", "responsibilities", "profile", "offer"],
    },
    2: { validationfields: [] },
    3: { validationfields: [] },
  };

  const [textArea, setTextArea] = useState({
    description: jobOffer?.description,
    responsibilities: jobOffer?.responsibilities,
    profile: jobOffer?.profile,
    offer: jobOffer?.offer,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});

  const [hardSkills, setHardSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [languageSkills, setLanguageSkills] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [licenceSkills, setLicenceSkills] = useState([]);
  const [isAbroad, setIsAbroad] = useState(jobOffer?.abroad || false);

  const INDUSTRY_CATEGORIES = {
    architecture: ARCHITECTURE_CATEGORIES,
    engineering: ENGINEERING_CATEGORIES,
    construction: CONSTRUCTION_CATEGORIES,
    administration: ADMINISTRATION_CATEGORIES,
    industry: INDUSTRY_SECTOR_CATEGORIES,
  };

  const availableCategories = values.company_industries?.length
    ? values.company_industries
      .flatMap((industry) => INDUSTRY_CATEGORIES[industry]?.[i18nLocale] || [])
      .reduce((unit, category) => {
        if (!unit.some((c) => c.slug === category.slug)) {
          unit.push(category);
        }
        return unit;
      }, [])
    : [];

  useEffect(() => {
    setValues(value => ({
      ...value,
      categories: value.categories.filter(slug =>
        availableCategories.some(category => category.slug === slug)
      ),
    }));
  }, [values.company_industries, i18nLocale]);

  useEffect(() => {
    setHardSkills(skills?.filter((skill) => skill.category == "hard"));
    setSoftSkills(skills?.filter((skill) => skill.category == "soft"));
    setLanguageSkills(skills?.filter((skill) => skill.category == "lang"));
    setSoftwareSkills(skills?.filter((skill) => skill.category == "software"));
    setLicenceSkills(skills?.filter((skill) => skill.category == "licence"));
  }, [skills.length]);

  const toggleExternalApplication = () => {
    setValues({
      ...values,
      ["external_application"]: !values["external_application"],
    });
  };

  const handleValidityValue = (event, value) => {
    setValues({
      ...values,
      ["days_validity"]: value,
    });
  };

  const validate = (fieldValues = {}) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );
    let end_date_must_be_higher = (
      <FormattedMessage id="form_validation.end_date_must_be_higher"></FormattedMessage>
    );
    let date_must_be_valid = (
      <FormattedMessage id="form_validation.date_must_be_valid"></FormattedMessage>
    );

    if (
      "offer_language" in fieldValues &&
      stepValues[currentStep].validationfields.includes("offer_language")
    ) {
      temp.offer_language = fieldValues.offer_language ? "" : required;
    }
    if (
      "company_industries" in fieldValues &&
      stepValues[currentStep].validationfields.includes("company_industries")
    ) {
      temp.company_industries = fieldValues.company_industries?.length
        ? ""
        : required;
    }
    if (
      "categories" in fieldValues &&
      stepValues[currentStep].validationfields.includes("categories")
    ) {
      temp.categories = fieldValues.categories?.length ? "" : required;
    }
    if (
      "job_title" in fieldValues &&
      stepValues[currentStep].validationfields.includes("job_title")
    ) {
      temp.job_title = fieldValues.job_title ? "" : required;
    }
    if (stepValues[currentStep].validationfields.includes("description")) {
      temp.description =
        textArea.description && getHtmlText(textArea.description)
          ? ""
          : required;
    } else {
      temp.description = "";
    }
    if (
      "job_statuses" in fieldValues &&
      stepValues[currentStep].validationfields.includes("job_statuses")
    ) {
      temp.job_statuses = fieldValues.job_statuses?.length > 0 ? "" : required;
    }

    if (
      "start_date" in fieldValues &&
      stepValues[currentStep].validationfields.includes("start_date")
    ) {
      if (
        fieldValues.start_date &&
        validator.isAfter(fieldValues.start_date, "01/01/2122")
      ) {
        temp.start_date = date_must_be_valid;
      } else {
        temp.start_date = "";
      }
    }

    if (
      "locations" in fieldValues &&
      stepValues[currentStep].validationfields.includes("locations")
    ) {
      temp.locations =
        fieldValues.abroad || fieldValues.locations?.length ? "" : required;
    }

    if (
      "external_application" in fieldValues ||
      "external_application_url" in fieldValues
    ) {
      const enabled =
        "external_application" in fieldValues
          ? fieldValues.external_application
          : values.external_application;
      const url =
        "external_application_url" in fieldValues
          ? fieldValues.external_application_url
          : values.external_application_url;
      temp.external_application_url = enabled && !url ? required : "";
    }

    if (
      "end_date" in fieldValues &&
      stepValues[currentStep].validationfields.includes("end_date") &&
      !values.job_statuses?.some(
        (status) => status === "permanent" || status === "freelance" || status === "interim"
      )
    ) {
      if (
        fieldValues.end_date &&
        values.start_date &&
        !validator.isAfter(fieldValues.end_date, values.start_date)
      ) {
        temp.end_date = end_date_must_be_higher;
      } else if (
        fieldValues.end_date &&
        validator.isAfter(fieldValues.end_date, "01/01/2122")
      ) {
        temp.end_date = date_must_be_valid;
      } else if (fieldValues.end_date) {
        temp.end_date = "";
      }
    }
    setErrors({
      ...temp,
    });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSkillChange = (category, value) => {
    let formattedValue = value?.map((elem) => {
      return {
        name: elem.id,
        category: category,
        company_id: elem.company_id,
      };
    });

    if (category === "hard") {
      setHardSkills(formattedValue);
    } else if (category === "soft") {
      setSoftSkills(formattedValue);
    } else if (category === "lang") {
      setLanguageSkills(formattedValue);
    } else if (category === "software") {
      setSoftwareSkills(formattedValue);
    } else if (category === "licence") {
      setLicenceSkills(formattedValue);
    }
  };

  //prev stands for the previous value
  const handleCardSelected = (name, value, multiple) => {
    const nextValue = multiple
      ? (values[name].includes(value)
        ? values[name].filter(earlier => earlier !== value)
        : [...values[name], value])
      : value;
    if (multiple) {
      setValues(previous =>
        previous[name].includes(value)
          ? { ...previous, [name]: previous[name].filter(earlier => earlier !== value) }
          : { ...previous, [name]: [...previous[name], value] }
      );
    } else {
      setValues(previous => ({
        ...previous,
        [name]: value,
        ...(name === "company_industries" ? {
          categories: previous.categories.filter(slug =>
            INDUSTRY_CATEGORIES[value]?.[i18nLocale]?.some(category => category.slug === slug)
          )
        } : {})
      }));
    }
    // Re-validate the changed field so its "required" error clears once a value is picked.
    validate({ [name]: nextValue });
  };

  useEffect(() => {
    if (values.job_statuses.includes("permanent") || values.job_statuses.includes("freelance") || values.job_statuses.includes("interim")) {
      setValues({ ...values, end_date: "" });
    }
  }, [values.job_statuses]);

  const onPlacesSelect = (place) => {
    if (place.address_components) {
      let compPostalCode = "";
      let compLocality = "";
      let compAdministrativeAreaLevel2 = "";
      let compAdministrativeAreaLevel1 = "";
      let countryShortName = "";
      let regionValue = "";

      place.address_components.forEach((category) => {
        if (category.types.includes("postal_code")) {
          compPostalCode = category.long_name;
        }
        if (category.types.includes("locality")) {
          compLocality = category.long_name;
        }
        if (category.types.includes("administrative_area_level_2")) {
          compAdministrativeAreaLevel2 = category.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (category.types.includes("administrative_area_level_1")) {
          compAdministrativeAreaLevel1 = category.long_name
            ?.toLowerCase()
            ?.replace(/ /g, "_");
        }
        if (category.types.includes("country")) {
          countryShortName = category.short_name.toLowerCase();
        }
      });

      if (countryShortName === "fr") {
        regionValue = compAdministrativeAreaLevel1 || compAdministrativeAreaLevel2;
        regionValue = normalizeRegion(regionValue);
      } else if (countryShortName === "be") {
        if (compAdministrativeAreaLevel2 === "luxembourg") {
          regionValue = "be_luxembourg";
        } else {
          regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
        }
      } else if (countryShortName === "lu") {
        regionValue = compAdministrativeAreaLevel1;
      } else {
        regionValue = compAdministrativeAreaLevel2 || compAdministrativeAreaLevel1;
      }

      let newLocation = {
        zipcode: compPostalCode,
        city: compLocality,
        region: regionValue?.replace(/-/g, "_"),
        district: compAdministrativeAreaLevel1?.replace(/-/g, "_"),
        country: countryShortName?.replace(/-/g, "_"),
      };

      let newValues = {
        ...values,
        locations: values.locations
          .map((location) => {
            return {
              zipcode: location.zipcode,
              city: location.city,
              region: location.region?.replace(/-/g, "_"),
              district: location.district?.replace(/-/g, "_"),
              country: location.country?.replace(/-/g, "_"),
            };
          })
          .concat([newLocation]),
      };

      setValues(newValues);
      validate({
        locations: newValues.locations,
      });
    }
  };

  const handleDeleteLocation = (deletedLocation) => {
    let newValues = {
      ...values,
      locations: values.locations.filter((location) => {
        return (
          Object.values(location).sort().join("") !=
          Object.values(deletedLocation.value).sort().join("")
        );
      }),
    };
    setValues(newValues);
  };

  const handleDeleteSkill = (deletedSkill, category) => {
    let skills;
    if (category === "hard") {
      skills = hardSkills.filter((value) => value.name != deletedSkill.value);
      setHardSkills(skills);
    } else if (category === "soft") {
      skills = softSkills.filter((value) => value.name != deletedSkill.value);
      setSoftSkills(skills);
    } else if (category === "lang") {
      skills = languageSkills.filter(
        (value) => value.name != deletedSkill.value
      );
      setLanguageSkills(skills);
    } else if (category === "software") {
      skills = softwareSkills.filter((value) => value.name != deletedSkill.value);
      setSoftwareSkills(skills);
    } else if (category === "licence") {
      skills = licenceSkills.filter((value) => value.name != deletedSkill.value);
      setLicenceSkills(skills);
    }
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    // Re-validate the touched field so its error clears as soon as it's filled.
    validate({ [name]: value });
  };

  const handleTextArea = (name) => (value) => {
    textArea[name] = value;
    setTextArea(textArea);
    validate();
  };

  // Patch a single staged media item by id (functional so background upload
  // callbacks stay correct even across step navigation / concurrent uploads).
  const updateStagedItem = (id, patch) => {
    setJobOffer((prev) => ({
      ...prev,
      staged_media: (prev.staged_media || []).map((item) =>
        item.id === id ? { ...item, ...patch } : item
      ),
    }));
  };

  // Kick off the S3 upload for a staged item immediately — no offer id needed.
  // The resulting blob_signed_id is attached to the offer at final save.
  const startStagedUpload = (item) => {
    uploadJobOfferMediaToS3(item.file, (pct) =>
      updateStagedItem(item.id, { progress: pct })
    )
      .then((blobSignedId) =>
        updateStagedItem(item.id, {
          status: "ready",
          blobSignedId,
          progress: 100,
        })
      )
      .catch(() => updateStagedItem(item.id, { status: "error" }));
  };

  const handleStageFiles = (files, kind) => {
    const additions = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      kind,
      previewUrl: URL.createObjectURL(file),
      status: "uploading",
      progress: 0,
      blobSignedId: null,
    }));
    setJobOffer((prev) => ({
      ...prev,
      staged_media: [...(prev.staged_media || []), ...additions],
    }));
    additions.forEach(startStagedUpload);
  };

  const handleRemoveStaged = (id) => {
    setJobOffer((prev) => {
      const target = (prev.staged_media || []).find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return {
        ...prev,
        staged_media: (prev.staged_media || []).filter((item) => item.id !== id),
      };
    });
  };

  const handleNextClicked = () => {
    const newValues = {
      ...values,
      ...textArea,
      offer_advantages: {
        advantages: advantages,
        environments: environments,
        schedules: schedules,
        primes: primes,
      },
      monthly_offer: value.join("-"),
      end_of_mandate: jobOffer.end_of_mandate,
      abroad: isAbroad,
      // Preserve uploaded media so it isn't dropped when moving between steps.
      job_offer_images_records: jobOffer.job_offer_images_records,
      job_offer_videos_records: jobOffer.job_offer_videos_records,
      // Preserve files staged before the offer exists, so they survive step changes
      // and can be uploaded at final save.
      staged_media: jobOffer.staged_media,
    };
    if (validate(newValues)) {
      const hard_skills = hardSkills.filter((skill) => !!skill.name);
      const soft_skills = softSkills.filter((skill) => !!skill.name);
      const languages_skills = languageSkills.filter((skill) => !!skill.name);
      const software_skills = (softwareSkills || []).filter((skill) => !!skill.name);
      const licence_skills = (licenceSkills || []).filter((skill) => !!skill.name);
      // Functional merge so media that background uploads / immediate uploads
      // wrote while this step was open isn't clobbered by the step snapshot.
      setJobOffer((prev) => ({
        ...newValues,
        staged_media: prev.staged_media,
        job_offer_images_records: prev.job_offer_images_records,
        job_offer_videos_records: prev.job_offer_videos_records,
      }));
      setSkills(hard_skills.concat(soft_skills, languages_skills, software_skills, licence_skills));
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      enqueueSnackbar(
        intl.formatMessage({ id: "form_validation.incomplete_form" }),
        { variant: "error" }
      );
      // Bring the first invalid field into view.
      setTimeout(() => {
        const firstError = document.querySelector(".Mui-error");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  function StepOne() {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography
            sx={{
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              fontSize: { xs: "1.125rem", md: "1.375rem" },
              color: "hsl(178, 93%, 10%)",
              letterSpacing: "-0.02em",
            }}
          >
            <FormattedMessage id="job_offer_creation.basic_informations" />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <TextField
            color="secondary"
            sx={textFieldSx}
            required
            label={<FormattedMessage id="job_offer.job_title" />}
            name="job_title"
            value={values.job_title}
            variant="outlined"
            onBlur={handleInputValue}
            onChange={handleInputValue}
            {...(errors["job_title"] && {
              error: true,
              helperText: errors["job_title"],
            })}
            inputProps={{ maxLength: 80 }}
          />
          <Box width={isDesktop ? "30%" : "100%"}>
            <Typography align="right">
              {values.job_title.length}/80{" "}
              {intl.formatMessage({ id: "characters" })}
            </Typography>
          </Box>
        </Grid>
        <Grid item
          xs={12}
          sx={formItemSx}
          align="left"
          style={{
            display: "flex",
            alignContent: "center",
            flexDirection: isDesktop ? "row" : "column",
            columnGap: isDesktop ? "20px" : "0px",
            rowGap: isDesktop ? "0px" : "8px",
          }}
        >
          <Typography variant="h5" style={{ paddingTop: "7px" }}>
            <FormattedMessage id="job_offer_creation.location_label" />
            {!isAbroad && <RequiredMark />}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={isAbroad}
                onChange={(e) => setIsAbroad(e.target.checked)}
              />
            }
            label={
              <Typography variant="h5">
                <FormattedMessage id="job_offer_creation.abroad_label" />
              </Typography>
            }
            style={{ paddingBottom: "5px" }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ mb: 0, overflowX: "hidden" }}
          align="left"
          style={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            columnGap: isDesktop ? "16px" : "0px",
            rowGap: isDesktop ? "0px" : "8px",
          }}
        >
          <TextField
            color="secondary"
            sx={textFieldSx}
            name="locations"
            variant="outlined"
            disabled={values.locations?.length >= 3 || isAbroad}
            placeholder={intl.formatMessage({
              id: "devise.signup.search_city_or_zipcode",
            })}
            InputProps={{
              inputComponent: ({ ...props }) => (
                <Autocomplete
                  /* eslint-disable no-undef */
                  apiKey={process.env.GOOGLE_AUTOCOMPLETE_API_KEY}
                  multiple
                  language="en"
                  {...props}
                  onPlaceSelected={onPlacesSelect}
                  options={{
                    types: ["(regions)"],
                    componentRestrictions: {
                      country: ["be", "lu", "fr"],
                    },
                  }}
                />
              ),
            }}
            {...(errors["locations"] && {
              error: true,
              helperText: errors["locations"],
            })}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            values={values.locations?.map((location) => {
              return {
                value: location,
                label: formatJobOfferLocation(location),
              };
            })}
            backgroundColor={theme.palette.secondary.main}
            color={theme.palette.common.white}
            maxSelection={3}
            onDelete={handleDeleteLocation}
          />
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            <FormattedMessage id="job_offer_creation.language_label" />
            <RequiredMark />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <CardSelect
            multiple={false}
            fieldName="offer_language"
            selected={values.offer_language}
            values={jobOfferLanguages.map((language) => {
              return {
                code: language,
                label: intl.formatMessage({ id: `languages.${language}` }),
              };
            })}
            onChange={handleCardSelected}
          />
          {errors["offer_language"] && (
            <p
              className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
              style={{ color: "#f44336" }}
            >
              {errors["offer_language"]}
            </p>
          )}
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            <FormattedMessage id="job_offer_creation.industry_label" />
            <RequiredMark />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <CardSelect
            multiple={true}
            fieldName="company_industries"
            selected={values.company_industries}
            values={companyIndustryMap.map((industry) => {
              return {
                code: industry,
                label: intl.formatMessage({
                  id: `company.company_industry.${industry}`,
                }),
              };
            })}
            onChange={handleCardSelected}
          />
          {errors["company_industries"] && (
            <p
              className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
              style={{ color: "#f44336" }}
            >
              {errors["company_industries"]}
            </p>
          )}
        </Grid>
        <>
          <Grid item xs={12} sx={formItemSx} align="left">
            <Typography variant="h5">
              <FormattedMessage id="job_offer_creation.category" />
              <RequiredMark />
            </Typography>
          </Grid>
          <Grid item xs={12} sx={formItemSx} align="left">
            <TextField
              select
              variant="outlined"
              color="secondary"
              name="categories"
              sx={textFieldSx}
              value={values.categories || []}
              error={!!errors.categories}
              onChange={element => {
                setValues(value => ({ ...value, categories: element.target.value }));
                setCatMenuOpen(false);
                validate({ categories: element.target.value });
              }}
              style={{marginBottom: "10px" }}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                open: catMenuOpen,
                onOpen: () => setCatMenuOpen(true),
                onClose: () => setCatMenuOpen(false),
                renderValue: selected =>
                  selected?.length
                    ? `${selected.length} ${intl.formatMessage({ id: "selected" })}`
                    : (
                      <em style={{ color: theme.palette.text.disabled }}>
                        {intl.formatMessage({ id: "select" })}
                      </em>
                    ),
              }}
            >
              {availableCategories.map(category => (
                <MenuItem key={category.slug} value={category.slug}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>

            {errors.categories && (
              <p className="MuiFormHelperText-root Mui-error">
                {errors.categories}
              </p>
            )}
            <ChipSelection
              values={(values.categories || []).map(slug => ({
                value: slug,
                label: availableCategories.find(category => category.slug === slug)?.label || slug,
              }))}
              backgroundColor={theme.palette.secondary.main}
              color={theme.palette.common.white}
              maxSelection={5}
              onDelete={chip =>
                setValues(previous => ({
                  ...previous,
                  categories: previous.categories.filter(slug => slug !== chip.value),
                }))
              }
            />
          </Grid>
        </>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            <FormattedMessage id="job_offer_creation.status_label" />
            <RequiredMark />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <CardSelect
            multiple={true}
            fieldName="job_statuses"
            selected={values.job_statuses}
            values={offerStatuses.map((status) => {
              if (status.tooltip) {
                return {
                  code: status.code,
                  label: (
                    <Tooltip
                      title={intl.formatMessage({
                        id: `candidate.status_map.${status.code}_hover`,
                      })}
                      style={{ cursor: "pointer" }}
                      placement="bottom"
                    >
                      <Box>
                        <span>
                          {intl.formatMessage({
                            id: `candidate.status_map.${status.code}`,
                          })}
                        </span>
                        <span>
                          <InfoIcon style={{ fontSize: "small" }} />
                        </span>
                      </Box>
                    </Tooltip>
                  ),
                };
              } else {
                return {
                  code: status.code,
                  label: intl.formatMessage({
                    id: `candidate.status_map.${status.code}`,
                  }),
                };
              }
            })}
            onChange={handleCardSelected}
          />
          {errors["job_statuses"] && (
            <p
              className="MuiFormHelperText-root MuiFormHelperText-sizeSmall Mui-error"
              style={{ color: "#f44336" }}
            >
              {errors["job_statuses"]}
            </p>
          )}
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            {values.job_statuses?.some(
              (status) => status === "permanent" || status === "freelance" || status === "interim"
            ) ? (
                <FormattedMessage id="job_offer_creation.date_label_start" />
              ) : (
                <FormattedMessage id="job_offer_creation.date_label" />
              )}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: "16px",
              maxWidth: "580px",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <DateSelect
                label={<FormattedMessage id="job_offer.start_date" />}
                value={values.start_date}
                onChange={(date) => {
                  setValues({ ...values, start_date: date });
                  validate({ start_date: date });
                }}
                error={!!errors["start_date"]}
                helperText={errors["start_date"]}
              />
            </Box>
            {!values.job_statuses?.some(
              (status) =>
                status === "permanent" ||
                status === "freelance" ||
                status === "interim"
            ) && (
              <Box sx={{ flex: 1 }}>
                <DateSelect
                  label={<FormattedMessage id="job_offer.end_date" />}
                  value={values.end_date}
                  minDate={values.start_date}
                  onChange={(date) => {
                    setValues({ ...values, end_date: date });
                    validate({ end_date: date });
                  }}
                  error={!!errors["end_date"]}
                  helperText={errors["end_date"]}
                />
              </Box>
            )}
          </Box>
          <Typography variant="body3" style={{ display: "flex", align: "center", marginTop: "10px" }}>
            <FormattedMessage id="asap_form" />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            <FormattedMessage id="job_offer_creation.remuneration_label" />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Box sx={{ maxWidth: "520px", mt: "8px" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: "8px" }}>
              <Box sx={valuePillSx}>
                {value[0]}K - {value[1]}K €
              </Box>
            </Box>
            <Box sx={{ px: "8px" }}>
              <Slider
                min={0}
                max={200}
                getAriaLabel={() => "monthly remuneration"}
                value={value}
                name="monthly_offer"
                onChange={handleChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}K`}
                sx={sliderSx}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={sliderBoundSx}>0K €</Typography>
                <Typography sx={sliderBoundSx}>200K €</Typography>
              </Box>
            </Box>
            <Typography variant="body3" style={{ display: "flex", justifyContent: "left", marginTop: "10px" }}>
              <FormattedMessage id="to_discuss_form" />
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography variant="h5">
            <FormattedMessage id="job_offer_creation.application_label" />
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Box style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!values.external_application}
                  color="secondary"
                  onChange={toggleExternalApplication}
                />
              }
              label={<FormattedMessage id="job_offer.external_application" />}
            />
            <Tooltip
              title={<FormattedMessage id="job_offer.squarehub_apply" />}
              style={{ cursor: "pointer" }}
              aria-label="add"
              placement="right"
            >
              <InfoIcon />
            </Tooltip>
          </Box>
          {values?.external_application && (
            <>
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Alert severity="error">
                  <FormattedMessage id="job_offer_creation.external_application_warning" />
                </Alert>
              </Stack>
              <TextField
                fullWidth
                required
                label={
                  <FormattedMessage id="job_offer.external_application_url" />
                }
                sx={{ mt: "8px", ...textFieldSx }}
                name="external_application_url"
                value={values.external_application_url}
                variant="outlined"
                color="secondary"
                onBlur={handleInputValue}
                onChange={handleInputValue}
                {...(errors["external_application_url"] && {
                  error: true,
                  helperText: errors["external_application_url"],
                })}
              />
            </>
          )}
        </Grid>

        {!jobOffer?.id && (
          <>
            <Grid item xs={12} sx={formItemSx} align="left">
              <Typography variant="h5">
                <FormattedMessage id="job_offer_creation.validity_label" />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={formItemSx} align="left">
              <Box sx={{ maxWidth: "520px", mt: "8px" }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: "8px" }}>
                  <Box sx={valuePillSx}>
                    {values.days_validity}
                    <FormattedMessage id="job_offer_creation.validity_unit" />
                  </Box>
                </Box>
                <Box sx={{ px: "8px" }}>
                  <Slider
                    min={1}
                    max={90}
                    valueLabelDisplay="auto"
                    defaultValue={90}
                    disableSwap
                    value={values.days_validity}
                    onChange={handleValidityValue}
                    sx={sliderSx}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={sliderBoundSx}>1</Typography>
                    <Typography sx={sliderBoundSx}>90</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    );
  }

  function StepTwo() {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container style={{ paddingBottom: "0px" }}>
            <Grid item xs={12} sx={{ mb: "24px" }} align="left">
              <Typography
                sx={{
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  fontSize: { xs: "1.125rem", md: "1.375rem" },
                  color: "hsl(178, 93%, 10%)",
                  letterSpacing: "-0.02em",
                }}
              >
                <FormattedMessage id="job_offer_creation.description" />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="h5">
                <b>
                  <FormattedMessage
                    id="job_offer_creation.summary_label"
                    values={{
                      b: (chunks) => <b>{chunks}</b>,
                    }}
                  />
                  <RequiredMark />
                </b>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="body1">
                <FormattedMessage
                  id="job_offer_creation.summary_sublabel"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={textareaItemSx}>
              <RichTextEditor
                onChange={handleTextArea("description")}
                defaultValue={jobOffer.description || textArea.description}
                {...(errors["description"] && {
                  error: true,
                  helperText: errors["description"],
                })}
              />
            </Grid>
            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="h5">
                <b>
                  <FormattedMessage
                    align="center"
                    id="job_offer_creation.responsibilities_label"
                  />
                </b>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="body1">
                <FormattedMessage
                  id="job_offer_creation.responsibilities_sublabel"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={textareaItemSx}>
              <RichTextEditor
                onChange={handleTextArea("responsibilities")}
                defaultValue={
                  jobOffer.responsibilities || textArea.responsibilities
                }
                {...(errors["responsibilities"] && {
                  error: true,
                  helperText: errors["responsibilities"],
                })}
              />
            </Grid>

            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="h5">
                <b>
                  <FormattedMessage
                    align="center"
                    id="job_offer_creation.profile_label"
                  />
                </b>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="body1">
                <FormattedMessage
                  id="job_offer_creation.profile_sublabel"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} sx={textareaItemSx}>
              <RichTextEditor
                onChange={handleTextArea("profile")}
                defaultValue={jobOffer.profile || textArea.profile}
                {...(errors["profile"] && {
                  error: true,
                  helperText: errors["profile"],
                })}
              />
            </Grid>

            <Grid item xs={12} sx={formItemSx}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="h5">
                <b>
                  <FormattedMessage
                    align="center"
                    id="job_offer_creation.offer_label"
                  />
                </b>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ mb: "16px" }}>
              <Typography
                align={isDesktop ? "left" : "center"}
                variant="body1">
                <FormattedMessage
                  id="job_offer_creation.offer_sublabel"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
                <Box
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <FormGroup>
                    <Typography
                      variant="h6"
                      style={{ marginBottom: "4px", marginTop: "16px" }}
                    >
                      <FormattedMessage id="job_offer_edit.advantages_title" />
                    </Typography>

                    {jobOfferAdvantages.advantages.map((advantage) => {
                      return (
                        <FormControlLabel
                          key={`advantage-${advantage}`}
                          control={
                            <Checkbox
                              disableRipple
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={advantages.includes(advantage)}
                            />
                          }
                          label={intl.formatMessage({
                            id: `job_offer_edit.advantages.${advantage}`,
                          })}
                          onChange={() => {
                            handleCheck(advantage);
                          }}
                        />
                      );
                    })}
                  </FormGroup>

                  <FormGroup>
                    <Typography
                      variant="h6"
                      style={{ marginBottom: "4px", marginTop: "16px" }}
                    >
                      <FormattedMessage id="job_offer_edit.environment_title" />
                    </Typography>
                    {jobOfferAdvantages.environment.map((environment) => {
                      return (
                        <FormControlLabel
                          key={`environment-${environment}`}
                          control={
                            <Checkbox
                              disableRipple
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={environments.includes(environment)}
                            />
                          }
                          label={intl.formatMessage({
                            id: `job_offer_edit.environment.${environment}`,
                          })}
                          onChange={() => {
                            handleCheckEnvironment(environment);
                          }}
                        />
                      );
                    })}
                  </FormGroup>

                  <FormGroup>
                    <Typography
                      variant="h6"
                      style={{ marginBottom: "4px", marginTop: "16px" }}
                    >
                      <FormattedMessage id="job_offer_edit.schedules_title" />
                    </Typography>
                    {jobOfferAdvantages.schedules.map((schedule) => {
                      return (
                        <FormControlLabel
                          key={`environment-${schedule}`}
                          control={
                            <Checkbox
                              disableRipple
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={schedules.includes(schedule)}
                            />
                          }
                          label={intl.formatMessage({
                            id: `job_offer_edit.schedules.${schedule}`,
                          })}
                          onChange={() => {
                            handleCheckSchedules(schedule);
                          }}
                        />
                      );
                    })}
                  </FormGroup>

                  <FormGroup>
                    <Typography
                      variant="h6"
                      style={{ marginBottom: "4px", marginTop: "16px" }}
                    >
                      <FormattedMessage id="job_offer_edit.prime_title" />
                    </Typography>

                    {jobOfferAdvantages.primes.map((prime) => {
                      return (
                        <FormControlLabel
                          key={`primes-${prime}`}
                          control={
                            <Checkbox
                              disableRipple
                              icon={checkboxIcon}
                              checkedIcon={checkboxCheckedIcon}
                              checked={primes.includes(prime)}
                            />
                          }
                          label={intl.formatMessage({
                            id: `job_offer_edit.primes.${prime}`,
                          })}
                          onChange={() => {
                            handleCheckPrimes(prime);
                          }}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={textareaItemSx}>
              <RichTextEditor
                onChange={handleTextArea("offer")}
                defaultValue={jobOffer.offer || textArea.offer}
                {...(errors["offer"] && {
                  error: true,
                  helperText: errors["offer"],
                })}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Photos & videos */}
        <Grid item xs={12} sx={formItemSx}>
          <JobOfferMediaFields
            jobOffer={jobOffer}
            onChange={(media) => setJobOffer((prev) => ({ ...prev, ...media }))}
            staged={jobOffer.staged_media || []}
            onStageFiles={handleStageFiles}
            onRemoveStaged={handleRemoveStaged}
          />
        </Grid>
      </Grid>
    );
  }

  function StepThree() {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sx={formItemSx} align="left">
          <Typography
            sx={{
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              fontSize: { xs: "1.125rem", md: "1.375rem" },
              color: "hsl(178, 93%, 10%)",
              letterSpacing: "-0.02em",
            }}
          >
            <FormattedMessage id="job_offer_creation.required_skills" />
          </Typography>
        </Grid>

        {/* Hard Skills */}
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="h5">
            <b>
              <FormattedMessage id="candidate.profile.skills.hard" />
            </b>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="body1">
            <FormattedMessage id="job_offer_creation.hard_skills_subtitle" />
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={formItemSx}
          style={{
            display: "flex",
            columnGap: "48px",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          <SkillSelect
            selectedValues={hardSkills.map((s) => s.name)}
            category="hard"
            label={intl.formatMessage({
              id: "candidate.profile.skills.hard",
            })}
            disabled={hardSkills.length >= 10}
            onChange={handleSkillChange}
            allowAddingCustomSkills={true}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            size="large"
            values={hardSkills?.map((s) => {
              return {
                value: s.name,
                label: s.company_id
                  ? s.name
                  : intl.formatMessage({
                    id: `skills_list.hard.${s.name}`,
                  }),
              };
            })}
            backgroundColor={theme.palette.secondary.main}
            color={theme.palette.common.white}
            maxSelection={10}
            category="hard"
            onDelete={(skill) => handleDeleteSkill(skill, "hard")}
          />
        </Grid>

        {/* Software Skills */}
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="h5"
            style={{ fontFamily: "Segoe UI Regular, sans-serif" }}
          >
            <b>
              <FormattedMessage id="candidate.profile.skills.software" />
            </b>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={formItemSx}
          style={{
            display: "flex",
            columnGap: "48px",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          <SkillSelect
            selectedValues={(softwareSkills || []).map((s) => s.name)}
            category="software"
            label={intl.formatMessage({
              id: "candidate.profile.skills.software",
            })}
            onChange={handleSkillChange}
            allowAddingCustomSkills={true}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            size="large"
            values={(softwareSkills || [])?.map((s) => {
              return {
                value: s.name,
                label: s.company_id
                  ? s.name
                  : intl.formatMessage({
                    id: `skills_list.software.${s.name}`,
                    defaultMessage: s.name,
                  }),
              };
            })}
            backgroundColor={theme.palette.secondary.main}
            color={theme.palette.common.white}
            category="software"
            onDelete={(skill) => handleDeleteSkill(skill, "software")}
          />
        </Grid>

        {/* Licence Skills */}
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="h5"
            style={{ fontFamily: "Segoe UI Regular, sans-serif" }}
          >
            <b>
              <FormattedMessage id="candidate.profile.skills.licence" />
            </b>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={formItemSx}
          style={{
            display: "flex",
            columnGap: "48px",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          <SkillSelect
            selectedValues={(licenceSkills || []).map((s) => s.name)}
            category="licence"
            label={intl.formatMessage({
              id: "candidate.profile.skills.licence",
            })}
            onChange={handleSkillChange}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            size="large"
            values={(licenceSkills || [])?.map((s) => {
              return {
                value: s.name,
                label: s.company_id
                  ? s.name
                  : intl.formatMessage({
                    id: `skills_list.licence.${s.name}`,
                    defaultMessage: s.name,
                  }),
              };
            })}
            backgroundColor={theme.palette.secondary.main}
            color={theme.palette.common.white}
            category="licence"
            onDelete={(skill) => handleDeleteSkill(skill, "licence")}
          />
        </Grid>

        {/* Soft Skills */}
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="h5">
            <b>
              <FormattedMessage id="candidate.profile.skills.soft" />
            </b>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="body1">
            <FormattedMessage id="job_offer_creation.soft_skills_subtitle" />
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={formItemSx}
          style={{
            display: "flex",
            columnGap: "48px",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          <SkillSelect
            selectedValues={softSkills.map((s) => s.name)}
            category="soft"
            label={intl.formatMessage({
              id: "candidate.profile.skills.soft",
            })}
            disabled={softSkills.length >= 10}
            onChange={handleSkillChange}
            allowAddingCustomSkills={true}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            size="large"
            values={softSkills?.map((s) => {
              return {
                value: s.name,
                label: s.company_id
                  ? s.name
                  : intl.formatMessage({
                    id: `skills_list.soft.${s.name}`,
                  }),
              };
            })}
            backgroundColor={theme.palette.primary.main}
            color={theme.palette.common.white}
            maxSelection={10}
            category="soft"
            onDelete={(skill) => handleDeleteSkill(skill, "soft")}
          />
        </Grid>

        {/* Lang Skills */}
        <Grid item xs={12} sx={formItemSx}>
          <Typography
            align={isDesktop ? "left" : "center"}
            variant="h5">
            <b>
              <FormattedMessage id="candidate.profile.skills.lang" />
            </b>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={formItemSx}
          style={{
            display: "flex",
            columnGap: "48px",
            flexDirection: isDesktop ? "row" : "column",
          }}
        >
          <SkillSelect
            selectedValues={languageSkills.map((s) => s.name)}
            category="lang"
            label={intl.formatMessage({
              id: "candidate.profile.skills.lang",
            })}
            disabled={languageSkills.length >= 3}
            onChange={handleSkillChange}
          />
          <ChipSelection
            style={{ marginTop: isDesktop ? "0px" : "8px" }}
            size="large"
            values={languageSkills?.map((s) => {
              return {
                value: s.name,
                label: s.company_id
                  ? s.name
                  : intl.formatMessage({
                    id: `skills_list.lang.${s.name}`,
                  }),
              };
            })}
            backgroundColor={theme.palette.text.hint}
            color={theme.palette.common.white}
            maxSelection={3}
            category="lang"
            onDelete={(skill) => handleDeleteSkill(skill, "lang")}
          />
        </Grid>
      </Grid>
    );
  }

  function StepFour() {
    return (
      <JobOfferSummary
        jobOffer={jobOffer}
        skills={skills || jobOffer.skills}
        isEdit={true}
        saveJobOffer={saveJobOffer}
        showNavigationMessage={setActiveBlocker}
      />
    );
  }

  return (
    <Box>
      {/* Progress stepper */}
      <Box
        sx={{
          width: { xs: "100%", md: "70%" },
          mx: "auto",
          mb: { xs: "28px", md: "44px" },
        }}
      >
        <ProgressStepper
          steps={stepperSteps}
          activeStep={currentStep}
          labels={stepperLabels}
        />
      </Box>

      {/* Step content */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: currentStep < 3 ? "1px solid hsl(160, 10%, 90%)" : "none",
          borderRadius: "12px",
          boxShadow:
            currentStep < 3 ? "0 1px 2px 0 rgba(0, 0, 0, 0.05)" : "none",
          p: currentStep < 3 ? { xs: "20px", md: "32px 40px" } : 0,
        }}
      >
        {currentStep === 0 && StepOne()}
        {currentStep === 1 && StepTwo()}
        {currentStep === 2 && StepThree()}
        {currentStep === 3 && StepFour()}
      </Box>

      {/* Footer navigation */}
      {currentStep < 3 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            justifyContent: "space-between",
            gap: "12px",
            mt: { xs: "24px", md: "32px" },
          }}
        >
          <Button
            variant="rounded"
            color="secondaryOutlined"
            sx={{ ...buttonSx, width: { xs: "100%", md: "180px" } }}
            onClick={
              currentStep === 0
                ? () => navigate(-1)
                : () => setCurrentStep(currentStep - 1)
            }
          >
            <FormattedMessage id={currentStep === 0 ? "cancel" : "back"} />
          </Button>
          <Button
            variant="rounded"
            color="secondaryContained"
            sx={{ ...buttonSx, width: { xs: "100%", md: "180px" } }}
            onClick={handleNextClicked}
          >
            <FormattedMessage id="next" />
          </Button>
        </Box>
      )}
      {currentStep === 3 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: "24px", md: "32px" },
            mb: "32px",
          }}
        >
          <Button
            variant="rounded"
            color="secondaryOutlined"
            sx={{ ...buttonSx, width: { xs: "100%", md: "280px" } }}
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            <FormattedMessage id="back" />
          </Button>
        </Box>
      )}
    </Box>
  );
}
